import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import GridOnIcon from '@mui/icons-material/GridOn';
import GroupIcon from '@mui/icons-material/Group';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Alert,
  Avatar,
  Button,
  FormControl,
  Link,
  MenuItem,
  Select,
  Skeleton,
  Tab,
  Theme,
} from '@mui/material';
import { ClassNameMap } from '@mui/styles';
import makeStyles from '@mui/styles/makeStyles';
import {
  GridRenderCellParams,
  GridRowData,
  GridRowModel,
} from '@mui/x-data-grid';
import clsx from 'clsx';
import { Formik, FormikProps } from 'formik';
import { DateTime, Duration } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { useLocation } from 'react-router-dom';
import { useRecoilStateLoadable, useRecoilValueLoadable } from 'recoil';
import * as XLSX from 'xlsx';
import * as Yup from 'yup';
import Axios from '../../../common/axiosInterceptor';
import {
  FormikSelect,
  MaterialAutocomplete,
} from '../../../common/formik-material-ui';
import {
  trackClickedDuplicateHandling,
  trackModalUpdates,
  trackSavePersonalPowerList,
  trackSaveTeamPowerList,
} from '../../../common/userTracking';
import { SettingsIcon } from '../../../images/CadenceIcons';
import hubspotLogo from '../../../images/crm-logos/hubspot-logo.jpeg';
import { AdvancedIcon, CrmIcon } from '../../../images/SettingsIcons';
import { Business } from '../../../lib/kixie-entities/tables/Business';
import { BusinessHarassmentPolicy } from '../../../lib/kixie-entities/tables/BusinessHarassmentPolicy';
import {
  IPowerlistHeaderMapping,
  Powerlist,
  PowerlistDispositionRule,
  PowerlistTeam,
} from '../../../lib/kixie-entities/tables/Powerlist';
import { agentUserId } from '../../../recoil/atoms/agentUserId';
import { endingDate } from '../../../recoil/atoms/endingDate';
import { startingDate } from '../../../recoil/atoms/startingDate';
import { IBusinessModel, IUserAtom, user } from '../../../recoil/atoms/user';
import {
  makePHPRequest,
  methodNameEnum,
} from '../../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../../services/rest/makeAPIRequest';
import TabLabel from '../general/TabLabel';
import { Advanced } from '../PowerlistSettings/Advanced';
import { CrmOwnershipRules } from '../PowerlistSettings/CrmOwnershipRules';
import { Dispositions } from '../PowerlistSettings/Dispositions';
import { Members } from '../PowerlistSettings/Members';
import { PowerlistHours } from '../PowerlistSettings/PowerlistHours';
import { Settings } from '../PowerlistSettings/Settings';
import { UploadFiles } from '../PowerlistSettings/UploadFiles';
import { getErrorTabIndex, isErrorInTab } from './DevicesAndRoutingModal';
import { PowerListMappingTool } from './PowerListMappingTool';
import { WizardModal } from './WizardModal';
// import { escape } from 'lodash';

const useStyles = makeStyles((theme: Theme) => ({
  dialogActions: {
    padding: '0px',
    display: 'initial',
    height: '40px',
    borderTop:
      theme.palette.mode === 'dark' ? '1px solid #737373' : '1px solid #77849D',
  },
  dialogPaper: {
    minHeight: '80vh',
    position: 'absolute',
    top: '1%',
  },
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
  dialogContent: {
    paddingTop: '10px',
  },
  title: {
    paddingLeft: '15px',
    paddingTop: '18px',
    paddingRight: '5px',
    paddingBottom: '7px',
    fontFamily: 'serif',
  },
  tabs: {},
  text: {
    color: theme.palette.mode === 'dark' ? 'black' : 'white',
    paddingLeft: '30px',
  },
  tab: {
    maxWidth: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTab-wrapper': {
      flexDirection: 'row',
    },
    '&[tabindex="0"]': {
      backgroundColor: theme.palette.divider,
    },
  },
  tabIcon: {
    marginRight: 15,
  },
  reset: {
    margin: 0,
    padding: 0,
  },
  userStatusDropdown: {
    marginTop: '1vh',
    marginBottom: 15,
  },
  dropdown: {
    marginLeft: '1vw',
    marginTop: '1vh',
  },
  alert: {
    marginBottom: 15,
  },
  validFormats: {
    paddingBottom: theme.spacing(2),
  },
  duplicateHandlingMappingButtonSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  duplicateHandlingMappingButtonRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  mappingButtonContainer: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'flex-end',
  },
  mappingButtonMessage: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  errorMessage: {
    color: 'red',
  },
  mappingButtonSuccess: {
    color: 'green',
  },
  mappingToolButton: {
    minWidth: 116,
    marginLeft: theme.spacing(1),
  },
  mappingUploadContainer: {
    margin: 0,
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: theme.spacing(1),
    overflow: 'hidden',
  },
  mappingUploadRow: {
    display: 'table-row',
  },
  mappingUploadCell: {
    display: 'table-cell',
    width: '33%',
    textAlign: 'center',
  },
  contactDataSourcesSelectorContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  contactDataSourcesSelector: {
    width: 300,
  },
  hubspotIcon: {
    display: 'inline-block',
    width: '1em',
    height: '1em',
    marginRight: 10,
  },
  spreadsheetIcon: { marginRight: 10 },
  contactListSelectorContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  contactListSelector: {
    width: 300,
  },
  contactPropertiesContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  contactPropertiesGrid: {
    display: 'grid',
    gridTemplateColumns: '[left] 220px [middle] 220px [right] 220px [end]',
  },
  contactPropertiesHeader: {
    textAlign: 'center',
  },
  contactPropertiesArrow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contactPropertiesProperty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contactPropertiesKixieField: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contactPropertiesAutocomplete: {
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  contactPropertiesSelect: {
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  contactPropertiesAddPhone: {
    alignSelf: 'start',
    marginTop: 15,
  },
}));

function convertMinutes(minutes: any) {
  minutes = Number(minutes);

  const daysHoursMinutes = {
    days: Math.floor(minutes / (60 * 24)),
    hours: Math.floor((minutes % (60 * 24)) / 60),
    minutes: Math.floor(minutes % 60),
  };

  return daysHoursMinutes;
}

interface IKixieLabeledField {
  value: string;
  label: string;
  matches: string[];
  allowPartialMatches: boolean;
  multiple?: boolean;
}

/* eslint-disable no-unused-vars */
enum KixieField {
  Ignore = '',
  PhoneNumber = 'Phone Number *',
  Email = 'Email',
  FirstName = 'First Name',
  LastName = 'Last Name',
  Title = 'Title',
  CompanyName = 'Company Name',
  NextCallRefresher = 'Next Call Refresher',
  CRMUserId = 'CRM User Id',
  CRMContactId = 'CRM Contact ID',
  Custom1 = 'Custom1',
  Custom2 = 'Custom2',
  Custom3 = 'Custom3',
  Custom4 = 'Custom4',
  Custom5 = 'Custom5',
  Custom6 = 'Custom6',
}
/* eslint-enable no-unused-vars */

const KixieLabeledField: Record<KixieField, IKixieLabeledField> = {
  [KixieField.Ignore]: {
    value: KixieField.Ignore,
    label: '(Ignore)',
    matches: [],
    allowPartialMatches: false,
  },
  [KixieField.PhoneNumber]: {
    value: KixieField.PhoneNumber,
    label: 'Phone Number *',
    matches: ['phone number', 'phone', 'mobile'],
    allowPartialMatches: true,
    multiple: true,
  },
  [KixieField.Email]: {
    value: KixieField.Email,
    label: 'Email',
    matches: ['e-mail', 'mail'],
    allowPartialMatches: true,
  },
  [KixieField.FirstName]: {
    value: KixieField.FirstName,
    label: 'First Name',
    matches: ['forename', 'fname'],
    allowPartialMatches: false,
  },
  [KixieField.LastName]: {
    value: KixieField.LastName,
    label: 'Last Name',
    matches: ['name', 'surname', 'lname'],
    allowPartialMatches: false,
  },
  [KixieField.Title]: {
    value: KixieField.Title,
    label: 'Title',
    matches: [],
    allowPartialMatches: false,
  },
  [KixieField.CompanyName]: {
    value: KixieField.CompanyName,
    label: 'Company Name',
    matches: ['company'],
    allowPartialMatches: false,
  },
  [KixieField.NextCallRefresher]: {
    value: KixieField.NextCallRefresher,
    label: 'Next Call Refresher',
    matches: ['note', 'notes', 'refresher'],
    allowPartialMatches: false,
  },
  [KixieField.CRMUserId]: {
    value: KixieField.CRMUserId,
    label: 'CRM User Id',
    matches: [],
    allowPartialMatches: false,
  },
  [KixieField.CRMContactId]: {
    value: KixieField.CRMContactId,
    label: 'CRM Contact ID',
    matches: [],
    allowPartialMatches: false,
  },
  [KixieField.Custom1]: {
    value: KixieField.Custom1,
    label: 'Custom1',
    matches: [],
    allowPartialMatches: false,
  },
  [KixieField.Custom2]: {
    value: KixieField.Custom2,
    label: 'Custom2',
    matches: [],
    allowPartialMatches: false,
  },
  [KixieField.Custom3]: {
    value: KixieField.Custom3,
    label: 'Custom3',
    matches: [],
    allowPartialMatches: false,
  },
  [KixieField.Custom4]: {
    value: KixieField.Custom4,
    label: 'Custom4',
    matches: [],
    allowPartialMatches: false,
  },
  [KixieField.Custom5]: {
    value: KixieField.Custom5,
    label: 'Custom5',
    matches: [],
    allowPartialMatches: false,
  },
  [KixieField.Custom6]: {
    value: KixieField.Custom6,
    label: 'Custom6',
    matches: [],
    allowPartialMatches: false,
  },
};

const toLower = (value) => value.toLowerCase();

Object.values(KixieLabeledField).forEach(
  (field) =>
    field.value !== KixieField.Ignore &&
    (field.matches = [field.label, ...field.matches].map(toLower))
);

const MAPPING_ERROR = {
  IS_MAPPING_COMPLETE: 'isMappingComplete',
  HAS_PHONE_NUMBER: 'hasPhoneNumber',
  IS_MAPPING_OPEN: 'isMappingOpen',
  HAS_NO_ROWS: 'hasNoRows',
};

const POWERLIST_MAPPING_LOCAL_STORAGE_KEY = 'powerlistMapping';

const HAS_NO_ROWS_ERROR_MESSAGE =
  'Missing data in file. Please select another file and try again.';

/* eslint-disable no-unused-vars */
enum ContactDataSource {
  HubSpot = 'hubspot',
  Spreadsheet = 'spreadsheet',
}
/* eslint-enable no-unused-vars */

interface IContactDataSource {
  crmid: number;
  name: string;
}

const CONTACT_DATA_SOURCE_NAMES: Record<string, string> = {
  [ContactDataSource.HubSpot]: 'HubSpot',
  [ContactDataSource.Spreadsheet]: 'Spreadsheet',
} as const;

const getContactDataSourceName = (dataSourceName: string) => {
  return CONTACT_DATA_SOURCE_NAMES[dataSourceName];
};

const CONTACT_DATA_SOURCE_LOGOS: Record<string, string | undefined> = {
  [ContactDataSource.HubSpot]: hubspotLogo,
  [ContactDataSource.Spreadsheet]: undefined,
} as const;

const getContactDataSourceLogo = (dataSourceName: string) => {
  return CONTACT_DATA_SOURCE_LOGOS[dataSourceName];
};

const DATA_SOURCE_CRM_ID: Record<ContactDataSource, number | undefined> = {
  [ContactDataSource.HubSpot]: 16,
  [ContactDataSource.Spreadsheet]: undefined,
} as const;

const getDataSourceCRMId = (
  dataSource: ContactDataSource | undefined
): number | undefined => {
  return dataSource ? DATA_SOURCE_CRM_ID[dataSource] : undefined;
};

const getDataSourceEndpoint = (
  url: string,
  crmId: number | undefined
): string => {
  return url.replace('/{crmid}', crmId ? `/${crmId}` : '');
};

const getContactListsEndpoint = (
  dataSource: ContactDataSource | undefined
): string => {
  const crmId = getDataSourceCRMId(dataSource);

  return getDataSourceEndpoint(endpointEnum.GET_POWERLIST_CONTACT_LISTS, crmId);
};

const getContactPropertiesEndpoint = (
  dataSource: ContactDataSource | undefined,
  mappingCrmId: number | undefined
): string => {
  const crmId = mappingCrmId || getDataSourceCRMId(dataSource);

  // outreach does not support fetching contact properties
  if (!crmId || mappingCrmId === MappingCRM.Outreach) return '';

  return getDataSourceEndpoint(
    endpointEnum.GET_POWERLIST_CONTACT_PROPERTIES,
    crmId
  );
};

/* eslint-disable no-unused-vars */
enum MappingCRM {
  HubSpot = 16,
  Outreach = 26,
}
/* eslint-enable no-unused-vars */

const MAPPING_CRMS: Record<MappingCRM, string> = {
  [MappingCRM.HubSpot]: 'HubSpot',
  [MappingCRM.Outreach]: 'Outreach',
} as const;

export const getMappingCRMName = (mappingCrm: MappingCRM | undefined) => {
  return mappingCrm ? MAPPING_CRMS[mappingCrm] : '';
};

export const getMappingCRMId = (
  row: GridRowModel | GridRowData
): MappingCRM | undefined => {
  if (row.isAgentPowerlist !== 1) return;

  if (row.dueCallTasksPowerlistCrmId) return row.dueCallTasksPowerlistCrmId;

  for (const mappingName of Object.values(MAPPING_CRMS)) {
    if (row.name.startsWith(`${mappingName} Due Call Tasks (`)) {
      return MappingCRM[mappingName];
    }
  }
};

export const nullIfHasMappingCrmId = (
  params: GridRenderCellParams,
  getValue: () => {}
) => {
  const mappingCrmId = getMappingCRMId((params as any).row);
  if (mappingCrmId) return '-';
  return getValue() ?? '-';
};

const OUTREACH_MAPPING_CONTACT_PROPERTIES: IContactProperty[] = [
  {
    label: 'Mobile Phones',
    name: 'mobilePhones',
    isPhone: true,
  },
  {
    label: 'Home Phones',
    name: 'homePhones',
    isPhone: true,
  },
  {
    label: 'Other Phones',
    name: 'otherPhones',
    isPhone: true,
  },
  {
    label: 'Voip Phones',
    name: 'voipPhones',
    isPhone: true,
  },
  {
    label: 'Work Phones',
    name: 'workPhones',
    isPhone: true,
  },
  {
    label: 'First Name',
    name: 'firstName',
    isPhone: false,
  },
  {
    label: 'Last Name',
    name: 'lastName',
    isPhone: false,
  },
  { label: 'Title', name: 'title', isPhone: false },
  { label: 'Company', name: 'company', isPhone: false },
  { label: 'Email', name: 'emails', isPhone: false },
  { label: 'Prospect Id', name: 'prospect.id', isPhone: false },
  { label: 'Outreach Sequence', name: 'sequence', isPhone: false },
];

const getCRMContactProperties = (
  mappingCrmId: number | undefined
): IContactProperty[] | undefined => {
  if (!mappingCrmId) return undefined;

  switch (mappingCrmId) {
    case MappingCRM.HubSpot:
      return undefined;
    case MappingCRM.Outreach:
      return OUTREACH_MAPPING_CONTACT_PROPERTIES;
    default:
      throw new Error(
        `getCRMContactProperties unimplemented for crm ${mappingCrmId}`
      );
  }
};

const getCRMContactMapping = (
  mappingCrmId: number | undefined
): IContactMapping[] => {
  if (!mappingCrmId) return [];

  switch (mappingCrmId) {
    case MappingCRM.HubSpot:
      return DEFAULT_HUBSPOT_CONTACT_MAPPING;
    case MappingCRM.Outreach:
      return DEFAULT_OUTREACH_CONTACT_MAPPING;
    default:
      throw new Error(
        `getCRMContactMapping unimplemented for crm ${mappingCrmId}`
      );
  }
};

export function PowerlistModal(props: {
  show: boolean;
  handleClose: () => void;
  enqueueSnackbarWithStyles: any;
  id: string | number | null;
  defaultTabIdx?: number;
  agent?: string | number;
  powerlistReportCache?: any;
}) {
  const classes = useStyles();

  const location = useLocation();

  const mappingCrmId = useMemo(() => {
    const mappingCrmId = Number(
      new URLSearchParams(location?.search).get('mappingCrmId')
    );
    return isNaN(mappingCrmId) ? undefined : mappingCrmId;
  }, [location]);

  const isMappingCRM = useMemo(() => {
    return Object.values(MappingCRM).includes(mappingCrmId ?? 0);
  }, [mappingCrmId]);

  const start = useRecoilValueLoadable<any>(startingDate);
  const end = useRecoilValueLoadable<any>(endingDate);
  const agentUserIdAtom = useRecoilValueLoadable(agentUserId)?.contents;
  const [file, setFile] = useState({} as any);
  const [mapping, setMapping] = useState<ContactMapping>(
    isMappingCRM ? getCRMContactMapping(mappingCrmId) : {}
  );
  const [initialMapping, setInitialMapping] = useState<ContactMapping>(mapping);
  const [hasSelectedFile, setHasSelectedFile] = useState(false);
  const [isMappingOpen, setIsMappingOpen] = useState(isMappingCRM);
  const [contactList, setContactList] = useState<IContactList | null>(null);
  const [chosenDataSource, setChosenDataSource] = useState<ContactDataSource>();
  const [hasMappedFields, setHasMappedFields] = useState(false);
  const [hasRows, setHasRows] = useState(false);
  const [useHeaderRowAsData, setUseHeaderRowAsData] = useState(false);
  const [firstSixRows, setFirstSixRows] = useState<String[][]>([]);
  const [mappingToolPreview, setMappingToolPreview] = useState<boolean>(false);
  const [previewContactIndex, setPreviewContactIndex] = useState(0);
  const [dateTimeFlags, setDateTimeFlags] = useState<Record<string, boolean>>({});

  const reader = new FileReader();
  const hasReadAsBinaryString = !!reader.readAsBinaryString;

  const extractFileHeadersToMapping = (
    headers: any[]
  ): IPowerlistHeaderMapping => {
    const result: IPowerlistHeaderMapping = {};
    for (const header of headers ?? []) {
      if (header) {
        result[header.toString().trim()] = KixieField.Ignore;
      }
    }
    return result;
  };

  const automapHeaders = (
    result: IPowerlistHeaderMapping
  ): IPowerlistHeaderMapping => {
    const lowercaseHeaders: Record<string, string> = {};
    const headers = {};

    for (const header of Object.keys(result)) {
      if (!header) continue;

      const lowercaseHeader = header.toLowerCase();
      lowercaseHeaders[lowercaseHeader] = lowercaseHeader;
      headers[lowercaseHeader] = header;
    }

    let phoneNumberMapped = false;

    const tryPartialMatch = (_value: string, matches: string[]) => {
      if (phoneNumberMapped && _value === KixieField.PhoneNumber) {
        return {
          isMatch: false,
          lowercaseHeaders: [KixieField.Ignore],
        };
      }
      const allMatchingLowercaseHeaders = new Set<string>();
      for (const match of matches) {
        const matchingLowercaseHeaders = Object.keys(lowercaseHeaders).filter(
          (h) => h.indexOf(match) !== -1
        );
        for (const matchingLowercaseHeader of matchingLowercaseHeaders) {
          allMatchingLowercaseHeaders.add(matchingLowercaseHeader);
        }
      }
      if (allMatchingLowercaseHeaders.size) {
        if (_value === KixieField.PhoneNumber) {
          phoneNumberMapped = true;
        }
        return {
          isMatch: true,
          lowercaseHeaders: Array.from(allMatchingLowercaseHeaders),
        };
      }
      return {
        isMatch: false,
        lowercaseHeaders: [KixieField.Ignore],
      };
    };

    const tryExactMatch = (_value: string, matches: string[]) => {
      if (phoneNumberMapped && _value === KixieField.PhoneNumber) {
        return {
          isMatch: false,
          lowercaseHeaders: [KixieField.Ignore],
        };
      }
      for (const match of matches) {
        const matchingLowercaseHeader = lowercaseHeaders[match];
        if (matchingLowercaseHeader) {
          if (_value === KixieField.PhoneNumber) {
            phoneNumberMapped = true;
          }
          return { isMatch: true, lowercaseHeaders: [matchingLowercaseHeader] };
        }
      }
      return {
        isMatch: false,
        lowercaseHeaders: [KixieField.Ignore],
      };
    };

    const remainingFieldValues = Object.values(KixieLabeledField).reduce(
      (acc, lf) => {
        if (lf.value) {
          acc[lf.value] = true;
        }
        return acc;
      },
      {}
    );

    for (const {
      value,
      matches,
      allowPartialMatches,
      multiple,
    } of Object.values(KixieLabeledField)) {
      if (!matches.length) continue;

      const hasRemaining = !!remainingFieldValues[value];
      if (!hasRemaining) continue;

      const exactMatch = tryExactMatch(value, matches);
      const partialMatch = allowPartialMatches
        ? tryPartialMatch(value, matches)
        : undefined;

      if (!exactMatch.isMatch && !partialMatch?.isMatch) continue;

      const matchingLowercaseHeaders = [
        ...exactMatch.lowercaseHeaders,
        ...(partialMatch?.lowercaseHeaders ?? []),
      ];

      if (!multiple) {
        delete remainingFieldValues[value];
        for (const matchingLowercaseHeader of matchingLowercaseHeaders) {
          delete lowercaseHeaders[matchingLowercaseHeader];
        }
      }

      for (const matchingLowercaseHeader of matchingLowercaseHeaders) {
        const originalHeader = headers[matchingLowercaseHeader];
        if (originalHeader !== undefined) {
          result[originalHeader] = value;
        }
      }
    }

    return result;
  };

  const isMatchingSavedMapping = (
    mapping: IPowerlistHeaderMapping,
    savedMapping: IPowerlistHeaderMapping
  ): boolean => {
    if (!mapping || !savedMapping) return false;

    const mappingKeys = Object.keys(mapping).sort();
    const savedMappingKeys = Object.keys(savedMapping).sort();
    const hasMatchingKeys =
      mappingKeys.length === savedMappingKeys.length &&
      mappingKeys.every((value, i) => value === savedMappingKeys[i]);

    return hasMatchingKeys;
  };

  const readExcelFile = (formik) => {
    const workbook = XLSX.read(reader.result, {
      type: hasReadAsBinaryString ? 'binary' : 'array',
      codepage: 65001, // UTF-8
    });

    const firstWorksheet = workbook.Sheets[workbook.SheetNames[0]];

    const rows = XLSX.utils.sheet_to_json(firstWorksheet, {
      header: 1,
    }) as string[][];

    setFirstSixRows(rows.slice(0, 6));

    const hasRows = rows.length >= 1 && rows[0].length >= 1;
    setHasRows(hasRows);
    if (!hasRows) {
      setTimeout(formik.validateForm, 1);
      return;
    }

    const savedMapping: IPowerlistHeaderMapping = JSON.parse(
      localStorage.getItem(POWERLIST_MAPPING_LOCAL_STORAGE_KEY) ?? '{}'
    );

    const headersMapping = extractFileHeadersToMapping(rows[0]);

    const autoMapping = automapHeaders(headersMapping);

    const mapping = isMatchingSavedMapping(headersMapping, savedMapping)
      ? savedMapping
      : autoMapping;

    const useHeaderRowAsData = Object.values(mapping).every(
      (mapped) => mapped === ''
    );

    setUseHeaderRowAsData(useHeaderRowAsData);
    setMapping(mapping);
    setInitialMapping(mapping);
    openMappingTool(formik);
  };

  const uploadFile = (file, formik) => {
    setFile(file);
    setHasSelectedFile(true);
    setHasMappedFields(false);

    reader.onload = () => readExcelFile(formik);

    hasReadAsBinaryString
      ? reader.readAsBinaryString(file)
      : reader.readAsArrayBuffer(file);
  };

  const updateMapping = (_event, element) => {
    const inputHeader = element?.props?.['data-input-header'];
    const outputHeader = element?.props?.value || KixieField.Ignore;

    setMapping({
      ...mapping,
      [inputHeader]: outputHeader,
    });
  };

  const updatePropertyMapping = (property: string, index: number) => {
    if (Array.isArray(mapping)) {
      const newMapping = mapping.reduce((mapping, oldValue, currentIndex) => {
        if (currentIndex === index) {
          mapping.push({ ...oldValue, property });
        } else {
          mapping.push(oldValue);
        }
        return mapping;
      }, [] as IContactMapping[]);

      setMapping(newMapping);
    }
  };

  const addPropertyMapping = (property: string, kixieField: KixieField) => {
    if (Array.isArray(mapping)) {
      setMapping([
        ...mapping,
        {
          property,
          kixieField,
        },
      ]);
    }
  };

  const isMappingIncomplete = () =>
    ((hasSelectedFile && hasRows) ||
      (!!contactList && !isEmptyList(contactList))) &&
    !hasMappedFields;

  const isMappingComplete = () =>
    ((hasSelectedFile && hasRows) ||
      (!!contactList && !isEmptyList(contactList))) &&
    hasMappedFields;

  const getMappingError = (formik) => {
    for (const mappingError of Object.values(MAPPING_ERROR)) {
      const formikError = formik.errors?.[mappingError];
      if (formikError) return formikError;
    }
    return null;
  };

  const hasMappingError = (formik) => !!getMappingError(formik);

  const openMappingTool = (formik) => {
    setIsMappingOpen(true);

    setTimeout(formik.validateForm, 1);
  };

  const closeMappingTool = (formik) => {
    setIsMappingOpen(false);

    setTimeout(formik.validateForm, 1);
  };

  const cancelMappingTool = (formik) => {
    setHasMappedFields(false);
    setMapping(initialMapping);

    closeMappingTool(formik);
  };

  const completeMappingTool = (formik) => {
    if (!hasPhoneNumber) {
      props.enqueueSnackbarWithStyles({
        message:
          'Phone Number is a required field. Please associate the Kixie Phone Number option.',
        alertProps: { severity: 'error' },
      });

      return;
    }

    setHasMappedFields(true);

    closeMappingTool(formik);

    saveMappingToLocalStorage();
  };

  const saveMappingToLocalStorage = () => {
    localStorage.setItem(
      POWERLIST_MAPPING_LOCAL_STORAGE_KEY,
      JSON.stringify(mapping)
    );
  };

  const handleChange = (formik: FormikProps<any>) => async (
    _event: any,
    newIndex: number
  ) => {
    setTabIndex(+newIndex);

    setTimeout(formik.validateForm, 1);
  };

  const [userLoadable, setUserAtom] = useRecoilStateLoadable<
    IUserAtom | Record<string, any>
  >(user);
  const userAtom = userLoadable.contents as IUserAtom;
  const role = userAtom.user?.role;
  const notUserRole =
    role === 'admin' || role === 'manager' || role === 'superadmin';
  const mutation = useMutation(getApiMutation(userAtom, setUserAtom));
  const tabToFields = [
    ['name'],
    [
      'powerlistHours',
      'targetHours',
      'useBusinessHoursForPowerlist',
      'useBusinessHoursForTarget',
    ],
    [
      MAPPING_ERROR.IS_MAPPING_COMPLETE,
      MAPPING_ERROR.HAS_PHONE_NUMBER,
      MAPPING_ERROR.IS_MAPPING_OPEN,
      MAPPING_ERROR.HAS_NO_ROWS,
    ],
    ['powerlistDispositionRules'],
    [],
  ];

  let getPowerlistEndpoint = `${endpointEnum.GET_POWERLIST}/${props.id}?businessid=${userAtom.businessid}&userid=${userAtom.userid}`;
  if (mappingCrmId) {
    getPowerlistEndpoint += `&crmid=${mappingCrmId}`;
  }

  const [
    powerlistCached,
    powerlistLoadable,
    setRefetchForPowerlist,
  ] = makeApiRequestCached<any>(
    {
      domain: process.env.VITE_APP_CDK_DOMAIN,
      stage: 'auto',
      endpoint: getPowerlistEndpoint,
      method: 'get',
      waitForPayload: !props.id,
    },
    (loadable, cached) =>
      loadable?.contents?.response?.data?.powerlist ?? cached.current
  );

  const hasPowerlistMapping = useMemo(() => {
    if (!powerlistCached.current) return undefined;

    return typeof powerlistCached.current?.mapping === 'object';
  }, [powerlistCached, powerlistLoadable]);

  const [tabIndex, setTabIndex] = useState(0);
  const [hasSetTabIndex, setHasSetTabIndex] = useState(false);
  useEffect(() => {
    if (!hasSetTabIndex && typeof hasPowerlistMapping === 'boolean') {
      setTabIndex(isMappingCRM && !hasPowerlistMapping ? 2 : 0);
      setHasSetTabIndex(true);
    }
  }, [
    tabIndex,
    isMappingCRM,
    hasPowerlistMapping,
    powerlistLoadable,
    hasSetTabIndex,
  ]);

  useEffect(() => {
    const powerlistMapping = powerlistCached.current?.mapping;
    if (!powerlistMapping) {
      return;
    }

    const fields: Record<string, string[]> = {};
    for (const [property, field] of Object.entries(powerlistMapping)) {
      const fieldStr = field?.toString?.() ?? '';
      if (fields[fieldStr]) {
        fields[fieldStr].push(property);
      } else {
        fields[fieldStr] = [property];
      }
    }

    const newMapping: IContactMapping[] = [];
    for (const [_index, { property, kixieField }] of Object.entries(mapping)) {
      const properties = fields[kixieField];

      let newProperty;
      if (properties?.includes(property)) {
        newProperty = property;
      } else if (properties?.length) {
        newProperty = properties?.[0];
      }

      if (newProperty) {
        properties?.splice(properties.indexOf(newProperty), 1);
      }

      newMapping.push({
        property: newProperty ?? IGNORED_OPTION.name,
        kixieField,
      });
    }
    setMapping(newMapping);
  }, [powerlistCached, powerlistLoadable]);

  const isAgentPowerlist: number | undefined =
    powerlistCached.current?.isAgentPowerlist || props.agent;

  const [
    _,
    businessDataLoadable,
    setRefetchForBusinessData,
  ] = makeApiRequestCached<IBusinessModel | undefined>(
    {
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_DATA,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0] ?? cached.current
  );

  const [businessCached, businessLoadable] = makeApiRequestCached<
    Business & {
      // eslint-disable-next-line func-call-spacing
      businessHarassmentPolicies: (BusinessHarassmentPolicy & {
        days: number;
        hours: number;
        minutes: number;
      })[];
    }
  >(
    {
      endpoint: `${endpointEnum.GET_BUSINESSES}/${userAtom?.businessid}`,
      method: 'get',
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.map((business: Business) => ({
        ...business,
        businessHarassmentPolicies: business.businessHarassmentPolicies?.map(
          (policy) => {
            const luxonDuration = Duration.fromMillis(
              policy.durationMin * 60 * 1000
            )
              .shiftTo('days', 'hours', 'minutes', 'seconds', 'milliseconds')
              .toObject();
            return {
              ...policy,
              days: luxonDuration.days,
              hours: luxonDuration.hours,
              minutes: luxonDuration.minutes,
            };
          }
        ),
      }))?.[0] ?? cached.current
  );

  const disposition = useRecoilValueLoadable(
    makePHPRequest({
      methodName: methodNameEnum.GET_USER_DISPOSITION_REPORTING,
      payloadJsonString: JSON.stringify({
        startdate: start.contents,
        enddate: end.contents,
        timezone: businessCached.current?.timezone?.timezonePhp,
      }),
      waitForPayload: !businessCached.current?.timezone?.timezonePhp,
    })
  );

  const dispoList = useMemo(
    () =>
      disposition.state === 'hasValue'
        ? disposition.contents?.data?.dispositions?.result
        : [],
    [disposition]
  );

  const businessHoursMemo = useMemo(
    () =>
      businessCached.current?.businessDetails?.businessHours ?? {
        1: {
          day: 'Monday',
          hours: [],
        },
        2: {
          day: 'Tuesday',
          hours: [],
        },
        3: {
          day: 'Wednesday',
          hours: [],
        },
        4: {
          day: 'Thursday',
          hours: [],
        },
        5: {
          day: 'Friday',
          hours: [],
        },
        6: {
          day: 'Saturday',
          hours: [],
        },
        7: {
          day: 'Sunday',
          hours: [],
        },
      },
    [businessLoadable]
  );

  const addNewPowerlistInitialValues = new Powerlist({
    powerlistId: null,
    name: '',
    status: true,
    autostartOnLogon: false,
    mustDespositionCalls: true,
    ignoreDNC: false,
    assignToAgentOnAnswer: false,
    overrideOwnerOnAnswer: false,
    onlyDialFromOwnerIfOwned: false,
    secondsBeforeTimeout: 15,
    powerlistPriority: 2,
    maxAttempts: 10,
    dialAtATime: 3,
    duplicateHandling: 'overwrite',
    maxAttemptsPerDay: 4,
    minDurationDays: 0,
    minDurationHours: 0,
    minDurationMinutes: 30,
    maxWaitAfterCallInSec: 180,
    nextPowerlistId: 0,
    powerlistHours: businessHoursMemo,
    useBusinessHoursForPowerlist: true,
    targetHours: businessHoursMemo,
    useBusinessHoursForTarget: true,
    maxSmsPerTemplateCount: 1,
    maxSmsPerTemplateDurationInMin: 1440,
    createdByUserId: props.agent ? props.agent : userAtom?.userid,
    isAgentPowerlist: props.agent ? 1 : 0,
    updatedDate: new Date().toISOString(),
    createdDate: new Date().toISOString(),
    useDefaultDispositionRules: false,
    powerlistDispositionRulesDeleted: [],
    powerlistDispositionRules: [
      {
        businessDispositionId: '',
        action: '',
        status: 'active',
        forwardToCadenceId: '',
        forwardToPowerlistId: '',
        smsTemplateId: '',
      } as any,
    ],
    powerlistSpreadsheets: [],
    maxSmsPerTemplateDurationInMinSub: 0,
    maxSmsPerTemplateDurationInHourSub: 0,
    maxSmsPerTemplateDurationInDaySub: 1,
    powerlistTeamsDeleted: [],
    powerlistTeams: props.agent
      ? [
          {
            powerlistTeamId: null,
            teamId: (agentUserIdAtom as any)?.systemTeamId,
            businessid: userAtom?.businessid,
            teamname: (agentUserIdAtom as any)?.name,
            memberCount: 1,
          },
        ]
      : [],
  } as any);

  const editPowerlistInitialValuesMemo = useMemo(
    () =>
      powerlistLoadable.state === 'hasValue' && powerlistCached?.current
        ? new Powerlist({
            powerlistId:
              powerlistCached.current?.powerlistId ?? Number(props.id),
            name: powerlistCached.current?.name ?? '',
            status: ((powerlistCached.current?.status ?? 'active') ===
              'active') as any,
            autostartOnLogon: !!powerlistCached.current?.autostartOnLogon,
            mustDespositionCalls: !!powerlistCached.current
              ?.mustDespositionCalls,
            ignoreDNC: !!powerlistCached.current?.ignoreDNC,
            assignToAgentOnAnswer: !!powerlistCached.current
              ?.assignToAgentOnAnswer,
            overrideOwnerOnAnswer: !!powerlistCached.current
              ?.overrideOwnerOnAnswer,
            onlyDialFromOwnerIfOwned: !!powerlistCached.current
              ?.onlyDialFromOwnerIfOwned,
            secondsBeforeTimeout:
              powerlistCached.current?.secondsBeforeTimeout ?? 30,
            powerlistPriority: powerlistCached.current?.powerlistPriority ?? 2,
            nextPowerlistId: powerlistCached.current?.nextPowerlistId ?? 0,
            maxAttempts: powerlistCached.current?.maxAttempts ?? 10,
            dialAtATime: powerlistCached.current?.dialAtATime ?? 1,
            duplicateHandling: 'overwrite',
            maxAttemptsPerDay: powerlistCached.current?.maxAttemptsPerDay ?? 2,
            minDurationDays: convertMinutes(
              powerlistCached.current?.minDurationBeforeCallRetryInMin ?? 0
            ).days,
            minDurationHours: convertMinutes(
              powerlistCached.current?.minDurationBeforeCallRetryInMin ?? 180
            ).hours,
            minDurationMinutes: convertMinutes(
              powerlistCached.current?.minDurationBeforeCallRetryInMin ?? 0
            ).minutes,
            maxWaitAfterCallInSec:
              powerlistCached.current?.maxWaitAfterCallInSec ?? 180,
            powerlistHours:
              powerlistCached.current?.powerlistHours ?? businessHoursMemo,
            useBusinessHoursForPowerlist: !!powerlistCached.current
              ?.useBusinessHoursForPowerlist,
            targetHours:
              powerlistCached.current?.targetHours ?? businessHoursMemo,
            useBusinessHoursForTarget: !!powerlistCached.current
              ?.useBusinessHoursForTarget,
            maxSmsPerTemplateCount:
              powerlistCached.current?.maxSmsPerTemplateCount ?? 1,
            maxSmsPerTemplateDurationInMin:
              powerlistCached.current?.maxSmsPerTemplateDurationInMin ?? 1440,
            isAgentPowerlist:
              powerlistCached.current?.isAgentPowerlist ?? props.agent ? 1 : 0,
            createdByUserId:
              powerlistCached.current?.createdByUserId ??
              (props.agent ? Number(props.agent) : userAtom?.userid),
            updatedDate:
              powerlistCached.current?.updatedDate ?? new Date().toISOString(),
            createdDate:
              powerlistCached.current?.createdDate ?? new Date().toISOString(),
            powerlistDispositionRulesDeleted: [],
            useDefaultDispositionRules:
              powerlistCached?.current?.useDefaultDispositionRules,
            powerlistDispositionRules: powerlistCached.current
              ?.powerlistDispositionRules?.length
              ? (powerlistCached.current?.powerlistDispositionRules?.map(
                  (dr) => ({
                    businessDispositionId: dr?.businessDispositionId ?? '',
                    action: dr?.action ?? '',
                    forwardToCadenceId: dr.forwardToCadenceId ?? '',
                    forwardToPowerlistId: dr.forwardToPowerlistId ?? '',
                    powerlistId: Number(props.id),
                    powerlistDispositionRuleId:
                      dr?.powerlistDispositionRuleId ?? '',
                    smsTemplateId: dr.smsTemplateId ?? '',
                    status: dr.status ?? 'active',
                  })
                ) as any)
              : ([
                  {
                    businessDispositionId: '',
                    action: '',
                    status: 'active',
                    forwardToCadenceId: '',
                    forwardToPowerlistId: '',
                    smsTemplateId: '',
                  },
                ] as any),
            powerlistSpreadsheets: [],
            maxSmsPerTemplateDurationInMinSub:
              (powerlistCached.current?.maxSmsPerTemplateDurationInMin ?? 0) %
              60,
            maxSmsPerTemplateDurationInHourSub: Math.floor(
              ((powerlistCached.current?.maxSmsPerTemplateDurationInMin ?? 0) %
                1440) /
                60
            ),
            maxSmsPerTemplateDurationInDaySub: Math.floor(
              (powerlistCached.current?.maxSmsPerTemplateDurationInMin ??
                1440) / 1440
            ),
            powerlistTeamsDeleted: [],
            powerlistTeams: powerlistCached.current?.powerlistTeams?.length
              ? (powerlistCached.current?.powerlistTeams?.map((dr) => ({
                  powerlistTeamId: dr?.powerlistTeamId ?? 0,
                  memberCount: dr?.memberCount ?? 0,
                  teamname: dr?.teamname ?? '',
                  teamId: dr.teamId ?? '',
                })) as any)
              : props.agent
              ? [
                  {
                    teamId: (agentUserIdAtom as any)?.systemTeamId,
                    businessid: userAtom?.businessid,
                  },
                ]
              : ([] as any),
          })
        : addNewPowerlistInitialValues,
    [powerlistLoadable, businessLoadable, props.id, agentUserIdAtom]
  );

  const tabNames = ([
    {
      title: 'Settings',
      icon: SettingsApplicationsIcon,
      key: 'settings',
    },
    {
      title: 'PowerList Hours',
      icon: QueryBuilderIcon,
      key: 'hours',
    },
    {
      title: isMappingCRM
        ? 'Mapping'
        : isMappingOpen
        ? 'Mapping Tool'
        : 'Contacts',
      description: isMappingCRM
        ? 'Mapping'
        : isMappingOpen
        ? 'Mapping Tool'
        : 'Add Contacts To PowerList',
      icon: CloudUploadOutlinedIcon,
      key: 'contacts',
    },
  ] as any[]).concat(
    isMappingCRM
      ? [
          {
            title: 'Advanced',
            icon: AdvancedIcon,
            key: 'advanced',
          },
        ]
      : [
          {
            title: 'Call Outcomes',
            icon: SettingsIcon,
            key: 'dispositions',
          },
          {
            title: 'Members',
            icon: GroupIcon,
            key: 'members',
          },
          {
            title: 'CRM Ownership Rules',
            icon: CrmIcon,
            key: 'crm',
          },
          {
            title: 'Advanced',
            icon: AdvancedIcon,
            key: 'advanced',
          },
        ]
  );
  const currentTabTitle = useMemo(() => {
    return tabNames[tabIndex]?.title ?? '';
  }, [tabIndex, tabNames]);

  function trackUpdates(
    values,
    editPowerlistInitialValuesMemo,
    currentTabTitle
  ) {
    trackModalUpdates(
      values,
      editPowerlistInitialValuesMemo,
      'PowerList Settings Updated',
      currentTabTitle
    );
  }

  const disablePowerlistToggle = useMemo(() => {
    return !businessDataLoadable.contents?.response?.data?.result?.[0]
      ?.allowAgentPowerlist;
  }, [businessDataLoadable]);

  const [
    contactDataSourcesCached,
    contactDataSourcesLoadable,
    setRefetchForContactDataSources,
  ] = makeApiRequestCached<any>(
    {
      method: 'POST',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      stage: 'auto',
      endpoint: endpointEnum.GET_POWERLIST_CONTACT_DATA_SOURCES,
      payloadJsonString: JSON.stringify({
        userid: userAtom?.userid,
      }),
      waitForPayload: isMappingCRM,
    },
    (loadable, cached) =>
      loadable.state === 'hasValue' ? loadable.contents : cached.current,
    []
  );

  const isContactDataSourcesLoading = useMemo(() => {
    return contactDataSourcesLoadable.state === 'loading';
  }, [contactDataSourcesLoadable]);

  const contactDataSources = useMemo(() => {
    if (isContactDataSourcesLoading) return [] as IContactDataSource[];

    const data = contactDataSourcesCached.current?.response?.data;

    const sources: IContactDataSource[] = Array.isArray(data) ? [...data] : [];

    // alphabetically sort contact data sources
    sources.sort((a, b) => {
      return a > b ? 1 : -1;
    });

    return sources;
  }, [isContactDataSourcesLoading, contactDataSourcesCached]);

  const hasChosenContactListsDataSource = useMemo(() => {
    return (
      !!chosenDataSource && chosenDataSource !== ContactDataSource.Spreadsheet
    );
  }, [chosenDataSource]);

  const hasChosenPowerlistSpreadsheetDataSource = useMemo(() => {
    return (
      !isContactDataSourcesLoading &&
      (chosenDataSource === ContactDataSource.Spreadsheet ||
        contactDataSources.length === 0)
    );
  }, [isContactDataSourcesLoading, chosenDataSource, contactDataSources]);

  const [
    contactListsCached,
    contactListsLoadable,
    setRefetchForContactLists,
  ] = makeApiRequestCached<any>(
    {
      method: 'POST',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      stage: 'auto',
      endpoint: getContactListsEndpoint(chosenDataSource),
      payloadJsonString: JSON.stringify({
        userid: userAtom?.userid,
      }),
      waitForPayload: !hasChosenContactListsDataSource,
      refetchParam: chosenDataSource,
    },
    (loadable, cached) =>
      loadable.state === 'hasValue' ? loadable.contents : cached.current,
    [],
    [chosenDataSource, hasChosenContactListsDataSource]
  );

  const isContactListsLoading = useMemo(() => {
    return contactListsLoadable.state === 'loading';
  }, [contactListsLoadable]);

  const contactLists = useMemo(() => {
    if (isContactListsLoading) return [] as IContactList[];

    const dataLists = contactListsCached.current?.response?.data?.lists;

    const lists: IContactList[] = Array.isArray(dataLists)
      ? [...dataLists]
      : [];

    // alphabetically sort contact lists
    lists.sort((a, b) => {
      return a.name > b.name ? 1 : -1;
    });

    return lists;
  }, [isContactListsLoading, contactListsCached]);

  const [
    contactPropertiesCached,
    contactPropertiesLoadable,
    setRefetchForContactProperties,
  ] = makeApiRequestCached<any>(
    {
      method: 'POST',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      stage: 'auto',
      endpoint: getContactPropertiesEndpoint(chosenDataSource, mappingCrmId),
      payloadJsonString: JSON.stringify({
        userid: userAtom?.userid,
      }),
      waitForPayload: !getContactPropertiesEndpoint(
        chosenDataSource,
        mappingCrmId
      ),
      refetchParam: chosenDataSource,
    },
    (loadable, cached) =>
      loadable.state === 'hasValue' ? loadable.contents : cached.current,
    [],
    [chosenDataSource, mappingCrmId]
  );

  // eslint-disable-next-line
  useEffect(() => {
    setRefetchForPowerlist?.({});
    setRefetchForBusinessData?.({});
    setRefetchForContactDataSources?.({});
    setRefetchForContactLists?.({});
    setRefetchForContactProperties?.({});
  }, []);

  const isContactPropertiesLoading = useMemo(() => {
    return contactPropertiesLoadable.state === 'loading';
  }, [contactPropertiesLoadable]);

  const contactProperties = useMemo(() => {
    if (isContactPropertiesLoading) return [];

    const contactProperties =
      getCRMContactProperties(mappingCrmId) ??
      contactPropertiesCached.current?.response?.data?.properties;

    const properties: IContactProperty[] = Array.isArray(contactProperties)
      ? [...contactProperties]
      : [];

    // alphabetically sort contact properties
    properties.sort((a, b) => {
      return a.label > b.label ? 1 : -1;
    });

    return properties;
  }, [isContactPropertiesLoading, contactPropertiesCached, mappingCrmId]);

  const defaultPropertiesMapping = useMemo(() => {
    return getDefaultPropertiesMapping({ chosenDataSource, contactProperties });
  }, [chosenDataSource, contactProperties]);

  const isContactDataSourcesSelectorVisible = useMemo(() => {
    return !isMappingOpen;
  }, [isMappingOpen]);

  const isContactSpreadsheetFileUploadVisible = useMemo(() => {
    return hasChosenPowerlistSpreadsheetDataSource && !isMappingOpen;
  }, [hasChosenPowerlistSpreadsheetDataSource, isMappingOpen]);

  const isContactSpreadsheetMappingToolVisible = useMemo(() => {
    return (
      !isMappingCRM && hasChosenPowerlistSpreadsheetDataSource && isMappingOpen
    );
  }, [isMappingCRM, hasChosenPowerlistSpreadsheetDataSource, isMappingOpen]);

  const isContactListsSelectorVisible = useMemo(() => {
    return hasChosenContactListsDataSource && !isMappingOpen;
  }, [hasChosenContactListsDataSource, isMappingOpen]);

  const isContactPropertiesMappingToolVisible = useMemo(() => {
    return (
      (isMappingCRM || (hasChosenContactListsDataSource && !!contactList)) &&
      isMappingOpen
    );
  }, [
    isMappingCRM,
    hasChosenContactListsDataSource,
    contactList,
    isMappingOpen,
  ]);

  const mappedKixieFields = useMemo(() => {
    if (
      !isMappingCRM &&
      hasChosenPowerlistSpreadsheetDataSource &&
      typeof mapping === 'object'
    ) {
      return Object.values(mapping);
    }

    if (hasChosenContactListsDataSource || isMappingCRM) {
      if (!Array.isArray(mapping)) {
        return [];
      }

      const propertyNames = contactProperties.map((property) => property.name);

      return mapping
        .filter(
          ({ property }) => !!property && propertyNames.includes(property)
        )
        .map(({ kixieField }) => kixieField);
    }

    return [];
  }, [
    hasChosenPowerlistSpreadsheetDataSource,
    hasChosenContactListsDataSource,
    isMappingCRM,
    mapping,
    contactProperties,
  ]);

  const hasPhoneNumber = useMemo(() => {
    return mappedKixieFields.includes(KixieField.PhoneNumber);
  }, [mappedKixieFields]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: isMappingCRM
          ? Yup.string()
          : Yup.string().required('Required').max(300, 'Char limit 300'),
        status: Yup.string().required('Required'),
        autostartOnLogon: Yup.boolean().required('Required'),
        mustDespositionCalls: Yup.boolean().required('Required'),
        ignoreDNC: Yup.boolean().required('Required'),
        assignToAgentOnAnswer: Yup.boolean().required('Required'),
        overrideOwnerOnAnswer: Yup.boolean().required('Required'),
        onlyDialFromOwnerIfOwned: Yup.boolean().required('Required'),
        secondsBeforeTimeout: Yup.number()
          .required('Required')
          .min(15, 'Minimum 15')
          .max(60, 'Max 60'),
        powerlistPriority: Yup.number()
          .required('Required')
          .min(0, 'Minimum: Lowest')
          .max(5, 'Max: Highest'),
        maxAttempts: Yup.number().required('Required').min(1, 'Minimum 1'),
        nextPowerlistId: Yup.number().nullable(),
        dialAtATime: Yup.number().required('Required'),
        maxAttemptsPerDay: Yup.number()
          .required('Required')
          .min(1, 'Minimum 1'),
        minDurationBeforeCallRetryInMin: Yup.number(),
        maxWaitAfterCallInSec: Yup.number()
          .required('Required')
          .min(30, 'Minimum 30')
          .max(300, 'Max 300'),
        powerlistDispositionRules: Yup.array().of(
          Yup.object().shape(
            {
              status: Yup.string(),
              action: Yup.string().when('businessDispositionId', {
                is: (val) => !!val,
                then: Yup.string().required('Selection is required'),
                otherwise: Yup.string(),
              }),
              businessDispositionId: Yup.number().when('action', {
                is: (val) => !!val,
                then: Yup.number().required('Selection is required'),
                otherwise: Yup.number(),
              }),
              forwardToCadenceId: Yup.number().when('action', {
                is: (val) => val === 'forwardToCadence',
                then: Yup.number().required('Selection is required'),
                otherwise: Yup.number(),
              }),
              forwardToPowerlistId: Yup.number().when('action', {
                is: (val) => val === 'forwardToPowerlist',
                then: Yup.number().required('Selection is required'),
                otherwise: Yup.number(),
              }),
              smsTemplateId: Yup.number().when('action', {
                is: (val) => val === 'sendSMS',
                then: Yup.number().required('Selection is required'),
                otherwise: Yup.number(),
              }),
            },
            [['action', 'businessDispositionId']]
          )
        ),
        [MAPPING_ERROR.IS_MAPPING_COMPLETE]: Yup.boolean().test({
          name: MAPPING_ERROR.IS_MAPPING_COMPLETE,
          message:
            "Mapping incomplete. Please select 'Mapping Tool' to proceed",
          test: () => isMappingCRM || isMappingOpen || !isMappingIncomplete(),
        }),
        [MAPPING_ERROR.HAS_PHONE_NUMBER]: Yup.boolean().test({
          name: MAPPING_ERROR.HAS_PHONE_NUMBER,
          message:
            isMappingCRM || hasChosenContactListsDataSource
              ? 'Must include at least one phone type to continue.'
              : 'Phone Number is a required field. Please associate the Kixie Phone Number option from your upload.',
          test: () =>
            !(
              (isMappingCRM || (!isMappingOpen && isMappingComplete())) &&
              !hasPhoneNumber
            ),
        }),
        [MAPPING_ERROR.IS_MAPPING_OPEN]: Yup.boolean().test({
          name: MAPPING_ERROR.IS_MAPPING_OPEN,
          message: 'Mapping incomplete. Please finish mapping to proceed',
          test: () => !(!isMappingCRM && isMappingOpen && tabIndex !== 2),
        }),
        [MAPPING_ERROR.HAS_NO_ROWS]: Yup.boolean().test({
          name: MAPPING_ERROR.HAS_NO_ROWS,
          message: HAS_NO_ROWS_ERROR_MESSAGE,
          test: () => !(hasSelectedFile && !hasRows),
        }),
      }),
    [
      tabIndex,
      mapping,
      hasSelectedFile,
      isMappingOpen,
      hasMappedFields,
      hasRows,
      hasPhoneNumber,
      isMappingCRM,
      hasChosenContactListsDataSource,
    ]
  );

  const loginUser = userAtom?.user ?? {};

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={async (values: any, actions) => {
        trackUpdates(values, editPowerlistInitialValuesMemo, currentTabTitle);
        const getHeaderMapping = (
          mapping: ContactMapping
        ): IPowerlistHeaderMapping => {
          if (Array.isArray(mapping)) {
            return mapping.reduce((mapping, { property, kixieField }) => {
              if (!!property) {
                mapping[property] = kixieField;
              }
              return mapping;
            }, {} as IPowerlistHeaderMapping);
          }

          if (typeof mapping === 'object') {
            return mapping as IPowerlistHeaderMapping;
          }

          return {};
        };

        if (values.powerlistSpreadsheets[0]) {
          const fileExt = file.name.split('.').pop();
          const blobData = new Blob([file], { type: file.type });
          const payload = {
            businessid: userAtom?.businessid,
            fileExt: fileExt,
            apiKey: userAtom?.apikey,
            userid: userAtom?.userid,
            contentType: file.type,
          };
          const signedURLResponse = await mutation.mutateAsync({
            endpoint: endpointEnum.GET_POWERLIST_SPREADSHEET_URL,
            method: 'post',
            omitBusinessIdAndApikey: true,
            payload,
          });
          await Axios.put(signedURLResponse.data.uploadUrl.uploadURL, blobData);
          values.powerlistSpreadsheets[0].powerlistSpreadsheetId = null;
          values.powerlistSpreadsheets[0].name = file.name;
          values.powerlistSpreadsheets[0].awsS3Key =
            signedURLResponse.data.uploadUrl.Key;
          values.powerlistSpreadsheets[0].status = `waitingToBeProcessed`;
          values.powerlistSpreadsheets[0].duplicateHandling =
            values.duplicateHandling;
          values.powerlistSpreadsheets[0].uploadedByUserId = userAtom?.userid;
          values.powerlistSpreadsheets[0].uploadDate = DateTime.now()
            .toUTC()
            .toISO();
        }
        values.powerlistDispositionRules = values.powerlistDispositionRules.filter(
          (rule: { businessDispositionId: string }) => {
            return !!rule.businessDispositionId;
          }
        );
        values.powerlistDispositionRules = values.powerlistDispositionRules.map(
          (rule) => {
            return {
              ...rule,
              forwardToCadenceId:
                rule.action === 'remove'
                  ? undefined
                  : rule.forwardToCadenceId
                  ? rule.forwardToCadenceId
                  : undefined,
              smsTemplateId:
                rule.action === 'remove'
                  ? undefined
                  : rule.smsTemplateId
                  ? rule.smsTemplateId
                  : undefined,
              forwardToPowerlistId:
                rule.action === 'remove'
                  ? undefined
                  : rule.forwardToPowerlistId
                  ? rule.forwardToPowerlistId
                  : undefined,
            };
          }
        );
        if (typeof powerlistCached.current != 'undefined') {
          // Build list of deleted teams
          const originalTeams: PowerlistTeam[] =
            powerlistCached.current.powerlistTeams;
          if (originalTeams?.length > 0) {
            const newTeams: PowerlistTeam[] = values.powerlistTeams;
            for (const orgTeam of originalTeams) {
              if (
                newTeams.findIndex(
                  (newTeam) =>
                    newTeam.powerlistTeamId === orgTeam.powerlistTeamId
                ) == -1
              ) {
                values.powerlistTeamsDeleted.push(orgTeam);
              }
            }
          }

          // Build list of deleted call outcome rules
          const originalDispositionRules: PowerlistDispositionRule[] =
            powerlistCached.current.powerlistDispositionRules;
          if (originalDispositionRules?.length > 0) {
            const newDispositionRules: PowerlistDispositionRule[] =
              values.powerlistDispositionRules;
            for (const dispRule of originalDispositionRules) {
              const matchIndex = newDispositionRules.findIndex(
                (newDispRule) =>
                  newDispRule.powerlistDispositionRuleId ===
                  dispRule.powerlistDispositionRuleId
              );
              if (matchIndex === -1) {
                values.powerlistDispositionRulesDeleted.push(dispRule);
              } else {
                if (
                  newDispositionRules[matchIndex]?.businessDispositionId !==
                  dispRule.businessDispositionId
                ) {
                  values.powerlistDispositionRulesDeleted.push(dispRule);
                }
              }
            }
          }
        }

        const name = isMappingCRM
          ? `${getMappingCRMName(mappingCrmId)} Due Call Tasks (${
              userAtom.userid
            })`
          : values.name;

        const getAgentUserPowerlistTeams = () =>
          ([] as any).concat(
            !values.powerlistId &&
              userAtom?.user?.systemTeamId &&
              userAtom?.businessid
              ? [
                  {
                    powerlistTeamId: null,
                    teamId: userAtom?.user?.systemTeamId,
                  },
                ]
              : []
          );

        const payload: Powerlist = new Powerlist({
          powerlistId: values.powerlistId,
          businessid: userAtom?.businessid,
          businessId: userAtom?.businessid,
          name: name,
          status: isMappingCRM || values.status ? 'active' : 'inactive',
          autostartOnLogon: isMappingCRM ? 0 : Number(values.autostartOnLogon),
          mustDespositionCalls: Number(values.mustDespositionCalls),
          ignoreDNC: Number(values.ignoreDNC),
          assignToAgentOnAnswer: isMappingCRM
            ? 0
            : Number(values.assignToAgentOnAnswer),
          overrideOwnerOnAnswer: isMappingCRM
            ? 0
            : Number(values.overrideOwnerOnAnswer),
          onlyDialFromOwnerIfOwned: isMappingCRM
            ? 0
            : Number(values.onlyDialFromOwnerIfOwned),
          secondsBeforeTimeout: values.secondsBeforeTimeout,
          powerlistPriority: values.powerlistPriority,
          nextPowerlistId: isMappingCRM ? undefined : values.nextPowerlistId,
          maxAttempts: isMappingCRM ? 1 : Number(values.maxAttempts),
          dialAtATime: values.dialAtATime,
          maxAttemptsPerDay: isMappingCRM ? 1 : values.maxAttemptsPerDay,
          minDurationBeforeCallRetryInMin: isMappingCRM
            ? 0
            : (values.minDurationDays ?? 0) * 60 * 24 +
              (values.minDurationHours ?? 0) * 60 +
              (values.minDurationMinutes ?? 0),
          maxWaitAfterCallInSec: values.maxWaitAfterCallInSec,
          powerlistHours: values.powerlistHours,
          useBusinessHoursForPowerlist: Number(
            values.useBusinessHoursForPowerlist
          ),
          targetHours: values.targetHours,
          useBusinessHoursForTarget: Number(values.useBusinessHoursForTarget),
          maxSmsPerTemplateCount: values.maxSmsPerTemplateCount,
          maxSmsPerTemplateDurationInMin:
            (values.maxSmsPerTemplateDurationInMinSub ?? 0) +
            (values.maxSmsPerTemplateDurationInHourSub ?? 0) * 60 +
            (values.maxSmsPerTemplateDurationInDaySub ?? 0) * 60 * 24,
          createdByUserId: values.createdByUserId,
          updatedDate: values.updatedDate,
          createdDate: values.createdDate,
          powerlistDispositionRulesDeleted:
            values.powerlistDispositionRulesDeleted,
          powerlistDispositionRules: values.powerlistDispositionRules,
          powerlistSpreadsheets: values.powerlistSpreadsheets,
          powerlistTeamsDeleted: values.powerlistTeamsDeleted,
          powerlistTeams: isMappingCRM
            ? getAgentUserPowerlistTeams()
            : values.powerlistTeams,
          isAgentPowerlist: isMappingCRM ? 1 : values.isAgentPowerlist,
          headerMapping: getHeaderMapping(mapping),
          useHeaderRowAsData: useHeaderRowAsData,
          dateTimeFlags: dateTimeFlags,
          hubspotContactListId: contactList?.listId,
          duplicateHandling: values.duplicateHandling,
          userid: userAtom?.userid,
          crmid: mappingCrmId,
          useDefaultDispositionRules: values.useDefaultDispositionRules ? 1 : 0,
        } as any);
        try {
          const res = await mutation.mutateAsync({
            domain: process.env.VITE_APP_CDK_DOMAIN,
            stage: 'auto',
            endpoint: endpointEnum.ADD_NEW_POWERLIST,
            method: 'post',
            payload,
          });

          let dislayMessage = (
            <span>
              Successfully {props.id ? 'Edited' : 'Added'} {name} Powerlist!
            </span>
          );
          if (res?.data?.data?.processingSpreadsheetAsynchronously == true) {
            dislayMessage = (
              <div>
                Successfully {props.id ? 'Edited' : 'Added'} "{name}" Powerlist!
                <br />
                Spreadsheet contacts are being processed in the background.
                <br />
                You can view the progress in the &nbsp;
                <a
                  href={`/manage/powerlists/${res?.data?.data?.powerlistId}/spreadsheets`}
                  style={{
                    color: 'white',
                    textDecoration: 'underline',
                  }}
                >
                  Upload Status Dialog
                </a>
              </div>
            );
          }

          if (res?.data?.success) {
            props.enqueueSnackbarWithStyles({
              message: dislayMessage,
              persist:
                res?.data?.data?.processingSpreadsheetAsynchronously == true,
              alertProps: { severity: 'success' },
            });
            setRefetchForPowerlist({});
            actions.resetForm({});
            props.handleClose();
          } else {
            let message = 'Encountered an error';
            let severity = 'error';
            if (
              res?.data?.code === 'ER_DUP_ENTRY' ||
              res?.data?.errorMessage?.includes('Duplicate entry')
            ) {
              message =
                'PowerList name already taken, please choose another name.';
              severity = 'warning';
            }
            if (
              res?.data?.errorMessage?.[0]?.[0].includes(
                'Missing required Columns'
              )
            ) {
              message =
                'Error uploading spreadsheet: missing required columns. Please ensure spreadsheet format is valid.';
            }
            props.enqueueSnackbarWithStyles({
              message,
              alertProps: { severity },
            });
          }
        } catch (ex: any) {
          let message = 'Encountered an error';
          let severity = 'error';

          if (
            ex.response.data.code === 'ER_DUP_ENTRY' ||
            ex.response?.data?.errorMessage?.includes('Duplicate entry')
          ) {
            message =
              'PowerList name already taken, please choose another name.';
            severity = 'warning';
          }

          props.enqueueSnackbarWithStyles({
            message,
            alertProps: { severity },
          });
        }
      }}
      initialValues={editPowerlistInitialValuesMemo}
    >
      {(formik) => {
        const filteredDispoList = useMemo(() => {
          return (
            [...(dispoList ?? [])].sort((a, b) =>
              a?.disposition
                ?.toString()
                ?.toLowerCase()
                ?.localeCompare(b?.disposition?.toString().toLowerCase())
            ) ?? []
          );
        }, [formik.values.powerlistDispositionRules, dispoList]);
        return (
          <form
            className="form form-label-right"
            onSubmit={() => {
              if (
                window.location.pathname ===
                `/my-profile/${loginUser.userid}/powerlists/add`
              ) {
                trackSavePersonalPowerList();
              } else if (window.location.pathname === '/manage/powerlists/') {
                trackSaveTeamPowerList();
              }
              formik.handleSubmit();
            }}
            noValidate
          >
            <TabContext value={String(tabIndex)}>
              <WizardModal
                className="powerlistModal"
                title={
                  tabNames[tabIndex ?? 0].description ??
                  tabNames[tabIndex ?? 0].title
                }
                size="lg"
                navigation={
                  <>
                    <Tab
                      label={
                        isMappingCRM
                          ? 'Due Call Tasks Configuration'
                          : props.id
                          ? 'Edit Powerlist'
                          : 'Add New Powerlist'
                      }
                      disabled={true}
                      className={clsx(classes.tab)}
                    />
                    <TabList
                      onChange={handleChange(formik)}
                      aria-label="Add New Powerlist"
                      orientation="vertical"
                      variant="standard"
                      className={clsx(classes.tabs, {})}
                    >
                      {tabNames.map((tabItem, index) => (
                        <Tab
                          data-pup={`powerlist-modal-${tabItem.key}`}
                          key={`tab-${index}`}
                          className={classes.tab}
                          label={
                            <TabLabel
                              index={index}
                              formik={formik}
                              tabToFields={tabToFields}
                              tabItem={tabItem}
                              title={
                                Boolean(
                                  isMappingCRM &&
                                    index === 2 &&
                                    formik.errors.hasPhoneNumber
                                )
                                  ? formik.errors.hasPhoneNumber
                                  : JSON.stringify(formik.errors)
                              }
                            />
                          }
                          value={String(index)}
                        />
                      ))}
                    </TabList>
                  </>
                }
                dialogActions={
                  <>
                    {!!isAgentPowerlist && tabIndex === 4 && !isMappingCRM && (
                      <p
                        style={{
                          marginRight: 'auto',
                          paddingLeft: '20px',
                        }}
                      >
                        ⓟ = Personal Powerlist
                      </p>
                    )}
                    {!isMappingOpen ? (
                      <>
                        <Button
                          className={classes.button}
                          disabled={formik.isSubmitting}
                          variant="contained"
                          color="inherit"
                          disableElevation={true}
                          onClick={() => {
                            props.handleClose();
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          data-pup="save-powerlist"
                          color="primary"
                          className={clsx(classes.button)}
                          variant="contained"
                          disableElevation={true}
                          disabled={
                            (isMappingCRM &&
                              (formik.isSubmitting || !formik.isValid)) ||
                            (!isMappingCRM &&
                              (formik.isSubmitting ||
                                !formik.dirty ||
                                !formik.isValid))
                          }
                          onClick={(event: any) => {
                            // trigger show errors if errors exists
                            formik.handleSubmit(event);
                            if (
                              !formik.isValid && // if form invalid
                              !isErrorInTab(
                                tabIndex ?? 0,
                                formik.errors,
                                tabToFields
                              ) // and error not in this tab
                            ) {
                              const errorTabIndex = getErrorTabIndex(
                                formik.errors,
                                tabToFields
                              );
                              setTimeout(() => setTabIndex(errorTabIndex ?? 0));
                            }
                          }}
                        >
                          Save
                        </Button>
                      </>
                    ) : isMappingOpen &&
                      (!chosenDataSource ||
                        chosenDataSource === ContactDataSource.Spreadsheet) &&
                      tabIndex === 2 ? (
                      mappingToolPreview === false ? (
                        <>
                          <Button
                            className={classes.button}
                            variant="contained"
                            color="inherit"
                            disableElevation={true}
                            onClick={() => {
                              setMappingToolPreview(false);
                              setPreviewContactIndex(0);
                              cancelMappingTool(formik);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            className={classes.button}
                            variant="contained"
                            disableElevation={true}
                            onClick={() => {
                              setMappingToolPreview(true);
                            }}
                            disabled={!hasPhoneNumber}
                          >
                            Preview
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            className={classes.button}
                            variant="contained"
                            color="inherit"
                            disableElevation={true}
                            onClick={() => {
                              setMappingToolPreview(false);
                              setPreviewContactIndex(0);
                              // cancelMappingTool(formik);
                            }}
                          >
                            Back
                          </Button>
                          <Button
                            color="primary"
                            className={classes.button}
                            variant="contained"
                            disableElevation={true}
                            onClick={() => {
                              setMappingToolPreview(false);
                              setPreviewContactIndex(0);
                              completeMappingTool(formik);
                            }}
                          >
                            Complete
                          </Button>
                        </>
                      )
                    ) : (
                      <>
                        <Button
                          className={classes.button}
                          variant="contained"
                          color="inherit"
                          disableElevation={true}
                          onClick={() => {
                            props.handleClose();
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          color="primary"
                          className={classes.button}
                          variant="contained"
                          disableElevation={true}
                          disabled={!hasPhoneNumber}
                          onClick={() => {
                            setMappingToolPreview(false);
                            setPreviewContactIndex(0);
                            completeMappingTool(formik);
                          }}
                        >
                          Complete
                        </Button>
                      </>
                    )}
                  </>
                }
                show={props.show}
                onHide={() => {
                  props.handleClose();
                }}
                onSubmit={formik.handleSubmit}
                isLoading={
                  powerlistLoadable.state === 'loading' ||
                  businessLoadable.state === 'loading' ||
                  disposition.state === 'loading'
                }
                isSubmitting={formik.isSubmitting}
              >
                <TabPanel value="0">
                  <Settings
                    id={Number(props.id)}
                    formik={formik}
                    isAgentPowerlist={isAgentPowerlist}
                    isMappingCRM={isMappingCRM}
                  />
                </TabPanel>
                <TabPanel value="1">
                  <PowerlistHours formik={formik} id={Number(props.id)} />
                </TabPanel>
                <TabPanel value="2">
                  {isContactSpreadsheetFileUploadVisible ? (
                    <Alert severity="info" className={classes.alert}>
                      Please select a file to upload. Once a file is selected,
                      you will be prompted to map your columns to the required
                      Kixie fields. Uploaded files must be mapped in order to
                      save your PowerList.
                    </Alert>
                  ) : isContactListsSelectorVisible ? (
                    <Alert severity="info" className={classes.alert}>
                      <ol className="mb-0">
                        <li>
                          If you don't see your CRM lists in the dropdown menu,
                          <Link
                            href="https://support.kixie.com/hc/en-us/articles/4422726101531-Connecting-your-CRM-to-Kixie"
                            underline="hover"
                            target="_blank"
                          >
                            &nbsp;re-connect your Kixie PowerCall to your
                            CRM&nbsp;
                          </Link>
                          and refresh this page.
                        </li>
                        {notUserRole ? (
                          <li>
                            If you select a CRM list and don't see the CRM
                            properties you'd like to map, then go to
                            <Link
                              href="/manage/account/#crm-properties"
                              underline="hover"
                            >
                              &nbsp;Account Settings {`>`} CRM Properties&nbsp;
                            </Link>
                            , select/save those CRM properties, and refresh this
                            page.
                          </li>
                        ) : (
                          <li>
                            If you select a CRM list and don't see the CRM
                            properties you'd like to map, then go to Account
                            Settings {`>`} CRM Properties, select/save those CRM
                            properties, and refresh this page. If you cannot
                            access this page, refer to a Manager or Admin on
                            your Kixie account.
                          </li>
                        )}

                        <li>
                          CRM Lists will be imported statically. If desired, you
                          can also create HubSpot workflows to add/remove
                          contacts from your CRM list into and out of this
                          PowerList.
                        </li>
                      </ol>
                    </Alert>
                  ) : isContactDataSourcesSelectorVisible ? (
                    <Alert severity="info" className={classes.alert}>
                      Please select either a spreadsheet import or a Contacts
                      list from your CRM as a data source.
                    </Alert>
                  ) : (
                    <></>
                  )}
                  {isContactDataSourcesSelectorVisible && (
                    <ContactDataSourcesSelector
                      classes={classes}
                      formik={formik}
                      chosenDataSource={chosenDataSource}
                      setChosenDataSource={setChosenDataSource}
                      setContactList={setContactList}
                      contactDataSources={contactDataSources}
                      isContactDataSourcesLoading={isContactDataSourcesLoading}
                      setMapping={setMapping}
                      setHasSelectedFile={setHasSelectedFile}
                      setIsMappingOpen={setIsMappingOpen}
                      setHasMappedFields={setHasMappedFields}
                      setHasRows={setHasRows}
                      setInitialMapping={setInitialMapping}
                    />
                  )}
                  {isContactSpreadsheetFileUploadVisible && (
                    <ContactSpreadsheetFileUpload
                      classes={classes}
                      formik={formik}
                      uploadFile={uploadFile}
                      hasMappingError={hasMappingError}
                      getMappingError={getMappingError}
                      isMappingComplete={isMappingComplete}
                      hasSelectedFile={hasSelectedFile}
                      openMappingTool={openMappingTool}
                      contactList={contactList}
                    />
                  )}
                  {isContactSpreadsheetMappingToolVisible && (
                    <PowerListMappingTool
                      useHeaderRowAsData={useHeaderRowAsData}
                      setUseHeaderRowAsData={setUseHeaderRowAsData}
                      mapping={mapping}
                      updateMapping={updateMapping}
                      firstSixRows={firstSixRows ?? []}
                      mappingToolPreview={mappingToolPreview}
                      previewContactIndex={previewContactIndex}
                      setPreviewContactIndex={setPreviewContactIndex}
                      dateTimeFlags={dateTimeFlags}
                      setDateTimeFlags={setDateTimeFlags}
                    />
                  )}
                  {isContactListsSelectorVisible && (
                    <ContactListsSelector
                      classes={classes}
                      formik={formik}
                      contactList={contactList}
                      setContactList={setContactList}
                      hasMappingError={hasMappingError}
                      getMappingError={getMappingError}
                      isMappingComplete={isMappingComplete}
                      hasSelectedFile={hasSelectedFile}
                      openMappingTool={openMappingTool}
                      setMapping={setMapping}
                      setHasMappedFields={setHasMappedFields}
                      contactLists={contactLists}
                      defaultPropertiesMapping={defaultPropertiesMapping}
                    />
                  )}
                  {isContactPropertiesMappingToolVisible && (
                    <ContactPropertiesMappingTool
                      classes={classes}
                      mapping={mapping}
                      updatePropertyMapping={updatePropertyMapping}
                      addPropertyMapping={addPropertyMapping}
                      isContactPropertiesLoading={isContactPropertiesLoading}
                      contactProperties={contactProperties}
                      chosenDataSource={chosenDataSource}
                      formik={formik}
                      isMappingCRM={isMappingCRM}
                      mappingCrmId={mappingCrmId}
                    />
                  )}
                </TabPanel>
                {!isMappingCRM && (
                  <>
                    <TabPanel value="3">
                      <Dispositions
                        filteredDispoList={filteredDispoList}
                        dispoList={dispoList}
                        formik={formik}
                        powerlistId={props.id ? Number(props?.id) : null}
                        isAgentPowerlist={isAgentPowerlist}
                      />
                    </TabPanel>
                    <TabPanel value="4">
                      <article>
                        <Members
                          id={Number(props.id)}
                          formik={formik}
                          isAgentPowerlist={isAgentPowerlist}
                          onTeamChange={() => {
                            setRefetchForPowerlist({});
                          }}
                        />
                      </article>
                    </TabPanel>
                    <TabPanel value="5">
                      <CrmOwnershipRules formik={formik} />
                    </TabPanel>
                  </>
                )}
                <TabPanel value={isMappingCRM ? '3' : '6'}>
                  <Advanced
                    formik={formik}
                    isMappingCRM={isMappingCRM}
                    isAgentPowerlist={isAgentPowerlist}
                    disablePowerlistToggle={disablePowerlistToggle}
                    id={Number(props.id)}
                  />
                </TabPanel>
              </WizardModal>
            </TabContext>
          </form>
        );
      }}
    </Formik>
  );
}

const ContactSpreadsheetFileUpload = ({
  classes,
  formik,
  uploadFile,
  hasMappingError,
  getMappingError,
  isMappingComplete,
  hasSelectedFile,
  openMappingTool,
  contactList,
}: {
  classes: ClassNameMap;
  formik: FormikProps<any>;
  uploadFile: (file: any, formik: FormikProps<any>) => void;
  hasMappingError: (formik: FormikProps<any>) => boolean;
  getMappingError: (formik: FormikProps<any>) => any;
  isMappingComplete: () => boolean;
  hasSelectedFile: boolean;
  openMappingTool: (formik: FormikProps<any>) => void;
  contactList: IContactList | null;
}) => {
  return (
    <>
      <div>Upload Data</div>
      <UploadFiles
        formik={formik}
        uploadFile={(file) => uploadFile(file, formik)}
      />
      <div className={classes.validFormats}>
        Valid Formats: .xls, .xlsx, .csv
      </div>
      <DuplicateHandlingMappingButtonSection
        classes={classes}
        formik={formik}
        hasMappingError={hasMappingError}
        getMappingError={getMappingError}
        isMappingComplete={isMappingComplete}
        hasSelectedFile={hasSelectedFile}
        openMappingTool={openMappingTool}
        contactList={contactList}
      />
    </>
  );
};

const ContactDataSourcesSelector = ({
  classes,
  formik,
  chosenDataSource,
  setChosenDataSource,
  setContactList,
  contactDataSources,
  isContactDataSourcesLoading,
  setMapping,
  setHasSelectedFile,
  setIsMappingOpen,
  setHasMappedFields,
  setHasRows,
  setInitialMapping,
}: {
  classes: ClassNameMap;
  formik: FormikProps<any>;
  chosenDataSource: ContactDataSource | undefined;
  setChosenDataSource: (dataSource: ContactDataSource | undefined) => void;
  setContactList: (list: IContactList | null) => void;
  contactDataSources: IContactDataSource[];
  isContactDataSourcesLoading: boolean;
  setMapping: (mapping: ContactMapping) => void;
  setHasSelectedFile: (hasSelectedFile: boolean) => void;
  setIsMappingOpen: (isMappingOpen: boolean) => void;
  setHasMappedFields: (hasMappedFields: boolean) => void;
  setHasRows: (hasRows: boolean) => void;
  setInitialMapping: (mapping: any) => void;
}) => {
  return (
    <div className={classes.contactDataSourcesSelectorContainer}>
      <FormControl
        className={classes.contactDataSourcesSelector}
        variant="outlined"
        fullWidth
        size="small"
      >
        {isContactDataSourcesLoading ? (
          <Skeleton
            variant="rectangular"
            style={{ marginTop: 8, height: 40 }}
          />
        ) : (
          <FormikSelect
            name="dataSource"
            value={
              (contactDataSources.length === 0
                ? ContactDataSource.Spreadsheet
                : chosenDataSource) ?? ''
            }
            disabled={contactDataSources.length === 0}
            formik={formik}
            label="Data Source"
            size="small"
            variant="outlined"
            fullWidth
            onChange={(event) => {
              const chosenDataSource = event.target.value as ContactDataSource;

              const defaultMapping = getDefaultMapping(chosenDataSource);

              const initialMapping =
                chosenDataSource === ContactDataSource.Spreadsheet
                  ? {}
                  : defaultMapping;

              setChosenDataSource(chosenDataSource);
              setContactList(null);
              setMapping(defaultMapping);
              setHasSelectedFile(false);
              setIsMappingOpen(false);
              setHasMappedFields(false);
              setHasRows(false);
              setTimeout(formik.validateForm, 1);
              setInitialMapping(initialMapping);
            }}
          >
            {contactDataSources.map((dataSource) => (
              <MenuItem
                value={dataSource.name}
                key={`contact-list-data-source-${dataSource.name}`}
              >
                {getContactDataSourceLogo(dataSource.name) && (
                  <Avatar
                    className={classes.hubspotIcon}
                    alt={getContactDataSourceName(dataSource.name)}
                    src={getContactDataSourceLogo(dataSource.name)}
                  />
                )}
                {getContactDataSourceName(dataSource.name)}
              </MenuItem>
            ))}
            <MenuItem value={ContactDataSource.Spreadsheet}>
              <GridOnIcon className={classes.spreadsheetIcon} />
              {getContactDataSourceName(ContactDataSource.Spreadsheet)}
            </MenuItem>
          </FormikSelect>
        )}
      </FormControl>
    </div>
  );
};

interface IContactList {
  listId: number;
  name: string;
  size: number;
}

interface IContactMapping {
  property: string;
  kixieField: KixieField;
}

type ContactMapping = IPowerlistHeaderMapping | IContactMapping[];

interface IContactProperty {
  label: string;
  name: string;
  isPhone: boolean;
}

const IGNORED_OPTION: IContactProperty = {
  name: KixieLabeledField[KixieField.Ignore].value,
  label: KixieLabeledField[KixieField.Ignore].label,
  isPhone: false,
};

const DEFAULT_HUBSPOT_CONTACT_MAPPING: IContactMapping[] = [
  { property: 'phone', kixieField: KixieField.PhoneNumber },
  { property: 'email', kixieField: KixieField.Email },
  { property: 'firstname', kixieField: KixieField.FirstName },
  { property: 'lastname', kixieField: KixieField.LastName },
  { property: 'jobtitle', kixieField: KixieField.Title },
  { property: 'company', kixieField: KixieField.CompanyName },
  { property: IGNORED_OPTION.name, kixieField: KixieField.NextCallRefresher },
  { property: 'hs_object_id', kixieField: KixieField.CRMContactId },
  { property: 'hubspot_owner_id', kixieField: KixieField.CRMUserId },
  { property: IGNORED_OPTION.name, kixieField: KixieField.Custom1 },
  { property: IGNORED_OPTION.name, kixieField: KixieField.Custom2 },
  { property: IGNORED_OPTION.name, kixieField: KixieField.Custom3 },
  { property: IGNORED_OPTION.name, kixieField: KixieField.Custom4 },
  { property: IGNORED_OPTION.name, kixieField: KixieField.Custom5 },
  { property: IGNORED_OPTION.name, kixieField: KixieField.Custom6 },
];

const DEFAULT_OUTREACH_CONTACT_MAPPING: IContactMapping[] = [
  { property: 'mobilePhones', kixieField: KixieField.PhoneNumber },
  { property: 'homePhones', kixieField: KixieField.PhoneNumber },
  { property: 'workPhones', kixieField: KixieField.PhoneNumber },
  { property: 'otherPhones', kixieField: KixieField.PhoneNumber },
  { property: 'voipPhones', kixieField: KixieField.PhoneNumber },
  { property: 'firstName', kixieField: KixieField.FirstName },
  { property: 'lastName', kixieField: KixieField.LastName },
  { property: 'title', kixieField: KixieField.Title },
  { property: 'company', kixieField: KixieField.CompanyName },
  { property: 'emails', kixieField: KixieField.Email },
  { property: IGNORED_OPTION.name, kixieField: KixieField.NextCallRefresher },
  { property: 'prospect.id', kixieField: KixieField.CRMContactId },
  { property: 'sequence', kixieField: KixieField.Custom1 },
  { property: IGNORED_OPTION.name, kixieField: KixieField.Custom2 },
  { property: IGNORED_OPTION.name, kixieField: KixieField.Custom3 },
  { property: IGNORED_OPTION.name, kixieField: KixieField.Custom4 },
  { property: IGNORED_OPTION.name, kixieField: KixieField.Custom5 },
  { property: IGNORED_OPTION.name, kixieField: KixieField.Custom6 },
];

const getDefaultMapping = (
  dataSource: ContactDataSource
): IContactMapping[] => {
  switch (dataSource) {
    case ContactDataSource.HubSpot:
      return DEFAULT_HUBSPOT_CONTACT_MAPPING;
    default:
      return [];
  }
};

const getDefaultPropertiesMapping = ({
  chosenDataSource,
  contactProperties,
}: {
  chosenDataSource: ContactDataSource | undefined;
  contactProperties: IContactProperty[];
}): ContactMapping => {
  if (!chosenDataSource) return {};

  const phoneProperties = contactProperties.filter(
    (property) => property.isPhone
  );

  const defaultMapping = getDefaultMapping(chosenDataSource);

  if (phoneProperties.length === 0) return defaultMapping;

  return [
    ...phoneProperties.map((property) => ({
      property: property.name,
      kixieField: KixieField.PhoneNumber,
    })),
    ...defaultMapping.filter(
      (mapping) => mapping.kixieField !== KixieField.PhoneNumber
    ),
  ];
};

const isEmptyList = (list: IContactList | null) => list && list.size === 0;

const ContactListsSelector = ({
  classes,
  formik,
  contactList,
  setContactList,
  hasMappingError,
  getMappingError,
  isMappingComplete,
  hasSelectedFile,
  openMappingTool,
  setMapping,
  setHasMappedFields,
  contactLists,
  defaultPropertiesMapping,
}: {
  classes: ClassNameMap;
  formik: FormikProps<any>;
  contactList: IContactList | null;
  setContactList: (list: IContactList | null) => void;
  hasMappingError: (formik: FormikProps<any>) => boolean;
  getMappingError: (formik: FormikProps<any>) => any;
  isMappingComplete: () => boolean;
  hasSelectedFile: boolean;
  openMappingTool: (formik: FormikProps<any>) => void;
  setMapping: (mapping: ContactMapping) => void;
  setHasMappedFields: (hasMappedFields: boolean) => void;
  contactLists: IContactList[];
  defaultPropertiesMapping: ContactMapping;
}) => {
  return (
    <>
      <div className={classes.contactListSelectorContainer}>
        <FormControl
          className={classes.contactListSelector}
          variant="outlined"
          fullWidth
          size="small"
        >
          <MaterialAutocomplete
            label="CRM List"
            type="text"
            size="small"
            variant="outlined"
            fullWidth
            multiple={false}
            formik={formik}
            options={contactLists}
            value={contactList}
            onChange={(_event, contactList) => {
              setContactList(contactList);
              setMapping(defaultPropertiesMapping);
              setHasMappedFields(false);

              const size = contactList?.size ?? 0;
              if (size > 0) {
                openMappingTool(formik);
              } else {
                setTimeout(formik.validateForm, 1);
              }
            }}
            getOptionLabel={(option) =>
              option ? `${option.name} (${option.size})` : ''
            }
            isOptionEqualToValue={(option, value) =>
              option?.name === value?.name
            }
          />
        </FormControl>
        {isEmptyList(contactList) && (
          <div className={classes.errorMessage}>
            We couldn't map your Contact properties because your list is empty.
          </div>
        )}
      </div>
      <DuplicateHandlingMappingButtonSection
        classes={classes}
        formik={formik}
        hasMappingError={hasMappingError}
        getMappingError={getMappingError}
        isMappingComplete={isMappingComplete}
        hasSelectedFile={hasSelectedFile}
        openMappingTool={openMappingTool}
        contactList={contactList}
      />
    </>
  );
};

const DuplicateHandlingMappingButtonSection = ({
  classes,
  formik,
  hasMappingError,
  getMappingError,
  isMappingComplete,
  hasSelectedFile,
  openMappingTool,
  contactList,
}: {
  classes: ClassNameMap;
  formik: FormikProps<any>;
  hasMappingError: (formik: FormikProps<any>) => boolean;
  getMappingError: (formik: FormikProps<any>) => any;
  isMappingComplete: () => boolean;
  hasSelectedFile: boolean;
  openMappingTool: (formik: FormikProps<any>) => void;
  contactList: IContactList | null;
}) => {
  return (
    <div className={classes.duplicateHandlingMappingButtonSection}>
      <div className={classes.duplicateHandlingMappingButtonRow}>
        <DuplicateHandlingSelect classes={classes} formik={formik} />
        <div className={classes.mappingButtonContainer}>
          <Button
            color="primary"
            variant="contained"
            disabled={
              (!hasSelectedFile &&
                (!contactList || isEmptyList(contactList))) ||
              getMappingError(formik) == HAS_NO_ROWS_ERROR_MESSAGE
            }
            className={classes.mappingToolButton}
            onClick={() => {
              openMappingTool(formik);
            }}
          >
            Mapping Tool
          </Button>
        </div>
      </div>
      <div className={classes.mappingButtonMessage}>
        {hasMappingError(formik) ? (
          <span className={classes.errorMessage}>
            {getMappingError(formik)}
          </span>
        ) : (
          isMappingComplete() && (
            <span className={classes.mappingButtonSuccess}>
              Mapping is complete. Ready to upload
            </span>
          )
        )}
      </div>
    </div>
  );
};

const DuplicateHandlingSelect = ({
  classes,
  formik,
}: {
  classes: ClassNameMap;
  formik: FormikProps<any>;
}) => {
  return (
    <FormControl
      className={classes.userStatusDropdown}
      size="small"
      variant="outlined"
      onClick={() => {
        trackClickedDuplicateHandling();
      }}
    >
      <FormikSelect
        name="duplicateHandling"
        style={{ width: '300px' }}
        defaultValue="overwrite"
        formik={formik}
        label="Duplicate Handling"
        size="small"
        variant="outlined"
        fullWidth
      >
        <MenuItem value="ignore">Ignore (First one handled wins)</MenuItem>
        <MenuItem value="overwrite">Overwrite (Duplicate Overwrites)</MenuItem>
        <MenuItem value="merge">
          Merge (Columns that are NOT empty in the spreadsheet will overwrite
          the data already uploaded previously)
        </MenuItem>
      </FormikSelect>
    </FormControl>
  );
};

const ContactPropertiesMappingTool = ({
  classes,
  mapping,
  updatePropertyMapping,
  addPropertyMapping,
  isContactPropertiesLoading,
  contactProperties,
  chosenDataSource,
  formik,
  isMappingCRM,
  mappingCrmId,
}: {
  classes: ClassNameMap;
  mapping: ContactMapping;
  updatePropertyMapping: (property: string, index: number) => void;
  addPropertyMapping: (property: string, kixieField: KixieField) => void;
  isContactPropertiesLoading: boolean;
  contactProperties: IContactProperty[];
  chosenDataSource: ContactDataSource | undefined;
  formik: FormikProps<any>;
  isMappingCRM: boolean;
  mappingCrmId: number | undefined;
}) => {
  const options = useMemo(() => [IGNORED_OPTION, ...contactProperties], [
    contactProperties,
  ]);

  const usedPropertyNames = useMemo(
    () =>
      Array.isArray(mapping)
        ? mapping
            .map((m) => m.property.toLowerCase())
            .filter((name) => name !== '')
        : [],
    [mapping]
  );

  return (
    <div className={classes.contactPropertiesContainer}>
      <Alert severity="info" className={classes.alert}>
        {isMappingCRM ? (
          <>
            Welcome to the PowerList Mapping Tool. Please match the Contact
            properties from {getMappingCRMName(mappingCrmId)} to the correct
            Kixie headers. NOTE: Mapping a phone number to the Kixie "Phone
            Number" field is required.
          </>
        ) : (
          <>
            Welcome to the PowerList Mapping Tool. Please match the Contact
            properties from your selected CRM list to the correct Kixie headers.
            NOTE: Mapping a phone number to the Kixie "Phone Number" field is
            required.
          </>
        )}
      </Alert>
      <div className={classes.contactPropertiesGrid}>
        <div className={classes.contactPropertiesHeader}>CRM Property</div>
        <div></div>
        <div className={classes.contactPropertiesHeader}>Kixie</div>
      </div>
      {Array.isArray(mapping) &&
        mapping.map(({ property, kixieField }, index) => (
          <div
            key={`${property}-${kixieField}-${index}`}
            className={classes.contactPropertiesGrid}
          >
            <div className={classes.contactPropertiesProperty}>
              {isContactPropertiesLoading ? (
                <Skeleton style={{ width: '100%', height: 50 }} />
              ) : (
                <MaterialAutocomplete
                  type="text"
                  size="small"
                  variant="outlined"
                  fullWidth
                  multiple={false}
                  options={options}
                  className={classes.contactPropertiesAutocomplete}
                  value={
                    contactProperties.find(
                      (contactProperty) => contactProperty.name === property
                    ) ?? IGNORED_OPTION
                  }
                  onChange={(_event, newValue) => {
                    updatePropertyMapping(
                      newValue?.name ?? IGNORED_OPTION.name,
                      index
                    );

                    setTimeout(formik.validateForm, 1);
                  }}
                  getOptionLabel={(option) => (option as any).label}
                  isOptionEqualToValue={(option, value) =>
                    option?.name === value?.name
                  }
                  filterOptions={(properties, state) => {
                    const inputValueLowerCased = state.inputValue.toLowerCase();
                    return properties.filter((property) => {
                      const propertyNameLowerCased = property.name.toLowerCase();
                      return (
                        !usedPropertyNames.includes(propertyNameLowerCased) &&
                        (state.inputValue === '' ||
                          propertyNameLowerCased.includes(
                            inputValueLowerCased
                          ) ||
                          property.label
                            .toLowerCase()
                            .includes(inputValueLowerCased))
                      );
                    });
                  }}
                />
              )}
            </div>
            <div className={classes.contactPropertiesArrow}>
              <img src="/media/misc/mapping-arrow.png" alt="arrow" />
            </div>
            <div className={classes.contactPropertiesKixieField}>
              {isContactPropertiesLoading ? (
                <Skeleton style={{ width: '100%', height: 50 }} />
              ) : (
                <Select
                  disabled
                  defaultValue={kixieField}
                  variant="outlined"
                  size="small"
                  fullWidth
                  className={classes.contactPropertiesSelect}
                >
                  <MenuItem value={kixieField} selected>
                    {kixieField}
                  </MenuItem>
                </Select>
              )}
            </div>
          </div>
        ))}
      {chosenDataSource === ContactDataSource.HubSpot && (
        <div className={classes.contactPropertiesAddPhone}>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();

              addPropertyMapping(IGNORED_OPTION.name, KixieField.PhoneNumber);

              return false;
            }}
          >
            Add Another Phone Number
          </a>
        </div>
      )}
    </div>
  );
};
